import { businessConfig } from "@/config/business.config";
import { services } from "@/data/services";

/**
 * JSON-LD structured data.
 *
 * Everything here is built from confirmed business facts only. Fields that
 * are not yet confirmed (a public street address, review ratings, geo
 * coordinates) are deliberately omitted rather than populated with invented
 * data, per project rules against fake locations and fake review schema.
 */

const abs = (path: string) => `${businessConfig.seo.baseUrl}${path}`;

// Stable @id so the various schema blocks can reference one another and
// Google can consolidate them into a single business entity.
const BUSINESS_ID = `${businessConfig.seo.baseUrl}/#business`;

export function getLocalBusinessSchema() {
  const { social, googleBusinessProfileUrl } = businessConfig.online;
  const sameAs = [googleBusinessProfileUrl, social.facebook, social.instagram].filter(
    (v) => v && !v.startsWith("TO CONFIRM"),
  );

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": ["ProfessionalService", "LocalBusiness"],
    "@id": BUSINESS_ID,
    name: businessConfig.identity.tradingName,
    legalName: businessConfig.identity.legalEntityName,
    description: businessConfig.seo.defaultDescription,
    url: businessConfig.seo.baseUrl,
    image: abs(businessConfig.seo.ogImage),
    logo: abs("/apple-touch-icon.png"),
    areaServed: [
      { "@type": "City", name: "Melbourne" },
      { "@type": "State", name: "Victoria" },
    ],
    serviceType: services.filter((s) => s.confirmed).map((s) => s.name),
    founder: {
      "@type": "Person",
      name: businessConfig.people.principal.name,
    },
    knowsAbout: ["Concrete cutting", "Core drilling", "Concrete sawing", "Concrete removal"],
    identifier: {
      "@type": "PropertyValue",
      propertyID: "ABN",
      value: businessConfig.identity.abn,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "07:00",
        closes: "17:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Sunday",
        opens: "09:00",
        closes: "17:00",
      },
    ],
  };

  if (businessConfig.contact.phoneConfirmed) {
    schema.telephone = "+61432068695";
  }
  if (businessConfig.contact.emailConfirmed) {
    schema.email = businessConfig.contact.email;
  }
  if (sameAs.length > 0) {
    schema.sameAs = sameAs;
  }
  if (businessConfig.contact.showAddress) {
    schema.address = {
      "@type": "PostalAddress",
      streetAddress: businessConfig.contact.address.line1,
      addressLocality: businessConfig.contact.address.suburb,
      addressRegion: businessConfig.contact.address.state,
      postalCode: businessConfig.contact.address.postcode,
      addressCountry: "AU",
    };
  } else {
    // Give Google a locality without publishing a street address.
    schema.address = {
      "@type": "PostalAddress",
      addressRegion: "VIC",
      addressCountry: "AU",
    };
  }

  // No aggregateRating / review block, no reviews exist yet, and none are
  // fabricated per project rules.

  return schema;
}

/** Per-service schema for the individual service detail pages. */
export function getServiceSchema(service: { name: string; shortDescription: string; slug: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.shortDescription,
    serviceType: service.name,
    url: abs(`/services/${service.slug}`),
    areaServed: [
      { "@type": "City", name: "Melbourne" },
      { "@type": "State", name: "Victoria" },
    ],
    provider: {
      "@type": "ProfessionalService",
      "@id": BUSINESS_ID,
      name: businessConfig.identity.tradingName,
    },
  };
}

/** Per-suburb landing page schema — the business's services offered in one locality. */
export function getLocationSchema(location: { name: string; slug: string; metaDescription: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Concrete Cutting & Coring — ${location.name}`,
    description: location.metaDescription,
    serviceType: "Concrete cutting, core drilling and sawing",
    url: abs(`/service-areas/${location.slug}`),
    areaServed: { "@type": "City", name: location.name, containedInPlace: { "@type": "State", name: "Victoria" } },
    provider: {
      "@type": "ProfessionalService",
      "@id": BUSINESS_ID,
      name: businessConfig.identity.tradingName,
      telephone: "+61432068695",
    },
  };
}

/** BreadcrumbList schema, helps Google render breadcrumb rich results. */
export function getBreadcrumbSchema(items: { label: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.label,
      item: abs(item.path),
    })),
  };
}

export function getFaqSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}
